import * as fuzzy from "fuzzy";
import * as React from "react";
import { Button, Input, Modal } from "react-ui";
import { useDebounce } from "use-debounce";

import { ICandyComponentHandle } from "../../../Engine/Core/ComponentHandleRegistry/ICandyComponentHandle";
import { IForm } from "../../../Engine/Form/IForm";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { BindingPath } from "../../../Engine/Core/CoreTypes";

import { Actions, ControlName, Path, PathHighlightedText, PathItem, PathValue } from "./SearchPathModal.styles";

interface SearchPathModalProps {
    onClose: () => void;
    form: IForm;
}

export function SearchPathModal(props: SearchPathModalProps): JSX.Element {
    const [searchText, setSearchText] = React.useState("");
    const [searchTextDebounced] = useDebounce(searchText, 200);

    const allPaths = React.useMemo(
        () =>
            Iterator.from(props.form.componentHandleRegistry.getBindingPaths())
                .filter(x => !x.includes("*"))
                .toArray(),
        []
    );
    const filterResults = React.useMemo(
        () => fuzzy.filter(searchTextDebounced, allPaths, { pre: "__<__", post: "__>__" }),
        [searchTextDebounced, allPaths]
    );

    return (
        <Modal onClose={props.onClose} alignTop={true}>
            <Modal.Header>
                <Input autoFocus={true} value={searchText} size={"large"} width={800} onValueChange={setSearchText} />
            </Modal.Header>
            <Modal.Body>
                {filterResults.map(x => (
                    <PathInfo
                        key={x.original}
                        bindingPath={x.original}
                        highlightedBindingPath={buildHighlightedReactElements(x.string)}
                        form={props.form}
                        onClose={props.onClose}
                    />
                ))}
            </Modal.Body>
        </Modal>
    );
}

function focusOnComponent(componentHandle: ICandyComponentHandle): void {
    componentHandle.focus();
}

interface PathInfoProps {
    bindingPath: BindingPath;
    highlightedBindingPath: React.ReactNode;
    form: IForm;
    onClose: () => void;
}

function PathInfo(props: PathInfoProps): JSX.Element {
    const control = React.useMemo(
        () => IterUtils.first(props.form.componentHandleRegistry.getComponentHandles(props.bindingPath) ?? []),
        [props.bindingPath]
    );
    const value = React.useMemo(() => props.form.getValue(props.bindingPath), [props.bindingPath]);

    const handleFocusClick = (): void => {
        setTimeout(() => {
            if (control != undefined) {
                focusOnComponent(control);
            }
        }, 0);
        props.onClose();
    };

    return (
        <PathItem>
            <Path>
                {props.highlightedBindingPath}
                <PathValue>({value ?? "--"})</PathValue>
            </Path>
            <ControlName>[{control?.constructor?.name ?? "unknown"}]</ControlName>
            <Actions>
                <Button use="link" onClick={handleFocusClick}>
                    focus
                </Button>
            </Actions>
        </PathItem>
    );
}

function buildHighlightedReactElements(stringWithHighlighted: string): React.ReactNode {
    const result: Array<React.ReactText | React.ReactElement> = [];
    const items = stringWithHighlighted.split(/(__<__|__>__)/);
    for (let i = 0; i < items.length; i++) {
        if (items[i] === "__<__") {
            result.push(<PathHighlightedText key={i.toString()}>{items[i + 1]}</PathHighlightedText>);
            i += 2;
        } else {
            result.push(items[i] ?? "");
        }
    }
    return result;
}
